import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Header from './Header';
import Footer from './Footer';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Info, LogOut, UserPlus, X, Store, ArrowLeftRight, Briefcase, User } from 'lucide-react';
import { useCommunity } from '@/contexts/CommunityContext';
import { useAuth } from '@/contexts/AuthContext';
import { useCommunityMember } from '@/contexts/CommunityMemberContext';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { useNotifications } from '@/hooks/useNotifications';
import { Badge } from '@/components/ui/badge';
import { CommunityAccessCheck } from './CommunityAccessCheck';
import { useTranslation } from 'react-i18next';
import { useBusiness } from '@/contexts/BusinessContext';
import { getUserDisplayName, getUserInitials, hasAdminDashboardAccess } from '@/utils/userUtils';
import BottomNavigationBar from './BottomNavigationBar';

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { currentCommunity } = useCommunity();
  const { user, signOut, userProfile } = useAuth();
  const { userRole } = useCommunityMember();
  const { unreadCount: totalUnreadCount } = useNotifications();
  const { t } = useTranslation();
  const { isBusinessOwner } = useBusiness();
  
  // Modified to ensure business owners don't see the admin dashboard
  const canAccessDashboard = hasAdminDashboardAccess(userRole, isBusinessOwner);

  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  const toggleMenu = () => {
    setIsOpen(!isOpen);
  };

  const handleLogout = async () => {
    await signOut();
    navigate('/');
    setIsOpen(false);
  };

  const handleBecomeProvider = () => {
    setIsOpen(false);
    if (!user) {
      navigate('/auth?returnTo=/register-business');
      return;
    }
    if (isBusinessOwner) {
      navigate('/admin/my-business');
    } else {
      navigate('/register-business');
    }
  };

  const pageVariants = {
    initial: {
      opacity: 0,
      y: 10,
    },
    in: {
      opacity: 1,
      y: 0,
    },
    out: {
      opacity: 0,
      y: -10,
    },
  };

  const pageTransition = {
    type: 'tween',
    ease: 'easeInOut',
    duration: 0.3,
  };
  
  return (
    <div className="flex flex-col min-h-screen bg-white">
      <Header toggleMenu={toggleMenu} isOpen={isOpen} />
      
      <AnimatePresence>
        {isOpen && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40 lg:hidden"
            onClick={() => setIsOpen(false)}
          />
        )}
      </AnimatePresence>
      
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'tween', duration: 0.3 }}
            className="fixed right-0 top-0 h-full w-72 bg-background z-50 shadow-xl p-5 lg:hidden"
          >
            <div className="flex justify-end">
              <button onClick={() => setIsOpen(false)} className="p-2">
                <X size={24} />
              </button>
            </div>
            
            {user && (
              <div className="mt-6 mb-6 p-4 border-b border-border">
                <div className="flex items-center">
                  <Avatar className="h-10 w-10 mr-3">
                    {userProfile?.avatar_url || user.user_metadata?.avatar_url ? (
                      <AvatarImage 
                        src={userProfile?.avatar_url || user.user_metadata?.avatar_url} 
                        alt={getUserDisplayName(user, userProfile)} 
                      />
                    ) : (
                      <AvatarFallback className="bg-primary text-primary-foreground">
                        {getUserInitials(user, userProfile)}
                      </AvatarFallback>
                    )}
                  </Avatar>
                  <div>
                    <div className="font-medium">
                      {getUserDisplayName(user, userProfile)}
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            <nav className="space-y-3">
              {/* Simplified burger menu - only sign up (when not authenticated), register business, and about us */}
              
              {/* Sign in - only shown when not authenticated */}
              {!user && (
                <Link 
                  to="/auth" 
                  onClick={() => setIsOpen(false)}
                  className="flex items-center w-full py-3 px-4 text-gray-700 hover:text-primary hover:bg-gray-50 rounded-lg transition-all duration-300 group"
                >
                  <User size={20} className="mr-3 text-gray-600 group-hover:text-primary group-hover:scale-110 transition-all duration-300" />
                  <span className="font-medium">{t('header.nav.signin') || 'Sign in'}</span>
                </Link>
              )}
              
              {/* Sign up - only shown when not authenticated */}
              {!user && (
                <Link 
                  to="/auth?mode=signup" 
                  onClick={() => setIsOpen(false)}
                  className="flex items-center w-full py-3 px-4 text-primary hover:text-primary/80 hover:bg-primary/5 rounded-lg transition-all duration-300 group"
                >
                  <UserPlus size={20} className="mr-3 group-hover:scale-110 transition-transform duration-300" />
                  <span className="font-medium">{t('header.nav.signup')}</span>
                </Link>
              )}
              
              {/* Request service - only shown when not authenticated */}
              {!user && (
                <Link 
                  to="/service-request" 
                onClick={() => setIsOpen(false)}
                  className="flex items-center w-full py-3 px-4 text-gray-700 hover:text-primary hover:bg-gray-50 rounded-lg transition-all duration-300 group"
                >
                  <ArrowLeftRight size={20} className="mr-3 text-gray-600 group-hover:text-primary group-hover:scale-110 transition-all duration-300" />
                  <span className="font-medium">{t('header.nav.service_request') || 'Request Service'}</span>
                </Link>
              )}
              
              {/* Register your business - shown for non-business users */}
              {(!isBusinessOwner || !user) && (
                <button 
                  onClick={handleBecomeProvider}
                  className="flex items-center w-full py-3 px-4 text-gray-700 hover:text-primary hover:bg-gray-50 rounded-lg transition-all duration-300 group"
                >
                  <Store size={20} className="mr-3 text-gray-600 group-hover:text-primary group-hover:scale-110 transition-all duration-300" />
                  <span className="font-medium">{t('header.nav.become_provider') || 'Register Your Business'}</span>
                </button>
              )}
              
              {/* My Service Requests - shown for authenticated users */}
              {user && (
                <Link 
                  to="/my-service-requests" 
                  onClick={() => setIsOpen(false)}
                  className="flex items-center w-full py-3 px-4 text-gray-700 hover:text-primary hover:bg-gray-50 rounded-lg transition-all duration-300 group"
                >
                  <ArrowLeftRight size={20} className="mr-3 text-gray-600 group-hover:text-primary group-hover:scale-110 transition-all duration-300" />
                  <span className="font-medium">{t('header.nav.my_service_requests')}</span>
                </Link>
              )}
              
              {/* My Business link - shown for authenticated users (admin users get this instead of dashboard since dashboard is in bottom bar) */}
              {user && (canAccessDashboard || isBusinessOwner) && (
                <Link 
                  to="/admin/my-business" 
                  onClick={() => setIsOpen(false)}
                  className="flex items-center w-full py-3 px-4 text-gray-700 hover:text-primary hover:bg-gray-50 rounded-lg transition-all duration-300 group"
                >
                  <Store size={20} className="mr-3 text-gray-600 group-hover:text-primary group-hover:scale-110 transition-all duration-300" />
                  <span className="font-medium">{t('header.nav.my_business') || 'My Business'}</span>
                </Link>
              )}
              
              {/* About us page - always last */}
              <Link 
                to="/about" 
                onClick={() => setIsOpen(false)}
                className="flex items-center w-full py-3 px-4 text-gray-700 hover:text-primary hover:bg-gray-50 rounded-lg transition-all duration-300 group"
              >
                <Info size={20} className="mr-3 text-gray-600 group-hover:text-primary group-hover:scale-110 transition-all duration-300" />
                <span className="font-medium">{t('header.nav.about') || 'About Us'}</span>
              </Link>
              
              {/* Sign out for authenticated users */}
              {user && (
                <div className="pt-4 border-t border-gray-200">
                  <button 
                    onClick={handleLogout}
                    className="flex items-center w-full py-3 px-4 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-all duration-300 group"
                  >
                    <LogOut size={20} className="mr-3 group-hover:scale-110 transition-transform duration-300" />
                    <span className="font-medium">{t('header.nav.signout')}</span>
                  </button>
                </div>
              )}
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
      
      <main className="flex-1 pt-28 lg:pt-16 pb-16 lg:pb-0 bg-white">
        <AnimatePresence mode="wait">
          <motion.div
            key={location.pathname}
            initial="initial"
            animate="in"
            exit="out"
            variants={pageVariants}
            transition={pageTransition}
            className="min-h-screen bg-white"
            style={{ 
              "--primary-color": currentCommunity.primary_color,
              "--accent-color": currentCommunity.secondary_color,
            } as React.CSSProperties}
          >
            <CommunityAccessCheck>
              {children}
            </CommunityAccessCheck>
          </motion.div>
        </AnimatePresence>
      </main>
      
      <Footer />
      
      {/* Bottom Navigation Bar - only on mobile */}
      <BottomNavigationBar />
    </div>
  );
};

export default Layout;
